import { useState, useEffect } from "react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, FileText, Info, Clock, Calendar, CreditCard } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { PhoneInput } from "@/components/PhoneInput";
import NotificationSettings from "@/components/NotificationSettings";
import AvailabilityConfig from "@/components/AvailabilityConfig";
import PropTypes from 'prop-types';

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { syncService } from "../services/SyncService";
import { useQuota } from "../hooks/useQuota";
import StorageUsageCard from "@/components/StorageUsageCard";
import { toPaise, toRupees } from '../utils/currency';

const QuotaBar = ({ resource, label, subtext }) => {
  const { limit, usage, isUnlimited, loading } = useQuota(resource);

  if (loading) return <div className="h-12 animate-pulse bg-slate-50 rounded mb-3"></div>;

  if (isUnlimited) return (
    <div className="mb-4">
      <div className="flex justify-between text-sm text-slate-700 mb-1">
        <span className="font-medium">{label}</span>
        <span className="text-emerald-600 font-bold text-xs bg-emerald-50 px-2 py-0.5 rounded-full">UNLIMITED</span>
      </div>
      <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 w-full" />
      </div>
      {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
    </div>
  );

  const percentage = Math.min(100, (usage / limit) * 100);
  const isExceeded = usage >= limit;

  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm text-slate-700 mb-1">
        <span className="font-medium">{label}</span>
        <span className={isExceeded ? "text-red-600 font-bold" : "text-slate-600"}>
          {usage} <span className="text-slate-400 font-normal">/ {limit}</span>
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${isExceeded ? 'bg-red-500' : 'bg-blue-600'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {subtext && <div className="text-xs text-slate-500 mt-1">{subtext}</div>}
    </div>
  );
};

QuotaBar.propTypes = {
  resource: PropTypes.string,
  label: PropTypes.string,
  subtext: PropTypes.string
};



// ...

// In SubscriptionDetails:
// Match the line with "clinic's" and replace it with escaped version
// Since replace_file_content cannot do multiple non-contiguous easily without multi, I will use multi_replace.
// Wait, I can do 3 separate calls or 1 multi call.
// I'll use multi_replace for Settings.jsx.



export default function Settings({ token, onLogout }) {

  const doctorId = localStorage.getItem('user_id');

  const dbProfile = useLiveQuery(() => doctorId ? db.users.get(doctorId) : null, [doctorId]);
  const dbSettings = useLiveQuery(() => doctorId ? db.settings.get(doctorId) : null, [doctorId]);

  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    email: "",
    qualification: "",
    city: "",
    registration_number: "",
    personal_availability: {},
    personal_slot_duration_minutes: 30
  });

  const [defaultDuration, setDefaultDuration] = useState(30);
  const [defaultFee, setDefaultFee] = useState("");

  useEffect(() => {
    if (dbProfile) {
      setProfile(dbProfile);
    }
  }, [dbProfile]);

  useEffect(() => {
    if (dbSettings?.default_appointment_duration) {
      setDefaultDuration(dbSettings.default_appointment_duration);
    }
    if (dbSettings?.default_consultation_fee !== undefined && dbSettings?.default_consultation_fee !== null) {
      setDefaultFee(toRupees(dbSettings.default_consultation_fee));
    }
  }, [dbSettings]);

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      const timestamp = new Date().toISOString();
      await db.users.update(doctorId, {
        ...profile,
        updated_at: timestamp
      });

      await syncService.addToQueue('update', 'doctor', { ...profile, id: doctorId });

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update profile");
    }
  };

  const updateSettings = async (settingsUpdate) => {
    try {
      const timestamp = new Date().toISOString();
      const currentSettings = dbSettings || {};
      const newSettings = {
        ...currentSettings,
        ...settingsUpdate,
        user_id: doctorId,
        updated_at: timestamp
      };

      await db.settings.put(newSettings);

      await syncService.addToQueue('update', 'settings', settingsUpdate);

      toast.success("Settings updated!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update settings");
    }
  };

  return (
    <div className="page-container" data-testid="settings-page">
      <Sidebar active="settings" onLogout={onLogout} />
      <div className="main-content">
        <div className="header">
          <h1>Settings</h1>
        </div>

        <div style={{ maxWidth: '800px' }}>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={20} />
              Profile Information
            </h2>
            <form onSubmit={updateProfile}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="profile-name-input"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <PhoneInput
                    id="phone"
                    data-testid="profile-phone-input"
                    value={profile.phone}
                    max={10}
                    disabled
                    readOnly
                    className="bg-slate-50 text-slate-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={profile.email}
                    disabled
                    style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                  />
                </div>
                <div>
                  <Label htmlFor="qualification">Qualification</Label>
                  <Input
                    id="qualification"
                    data-testid="profile-qualification-input"
                    value={profile.qualification}
                    onChange={(e) => setProfile({ ...profile, qualification: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="registration_number">Registration Number</Label>
                  <Input
                    id="registration_number"
                    data-testid="profile-registration-number-input"
                    placeholder="e.g., MCI-12345"
                    value={profile.registration_number || ""}
                    onChange={(e) => setProfile({ ...profile, registration_number: e.target.value })}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    data-testid="profile-city-input"
                    value={profile.city}
                    onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  />
                </div>
              </div>
              <Button
                data-testid="update-profile-button"
                type="submit"
                style={{ marginTop: '1.5rem', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
              >
                Save Changes
              </Button>
            </form>
          </div>

          {/* Appointment Preferences */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={20} />
              Appointment Preferences
            </h2>
            <div>
              <Label htmlFor="duration">Default Appointment Duration (minutes)</Label>
              <div className="flex gap-2 items-center mt-2">
                <Input
                  id="duration"
                  type="number"
                  step="1"
                  min="1"
                  max="480"
                  placeholder="Minutes"
                  value={defaultDuration}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || '';
                    setDefaultDuration(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === '-' || e.key === 'e') {
                      e.preventDefault();
                    }
                  }}
                  className="w-24"
                />
                <div className="flex gap-1">
                  {[15, 30, 45, 60].map((mins) => (
                    <Button
                      key={mins}
                      type="button"
                      variant={defaultDuration === mins ? "default" : "outline"}
                      size="sm"
                      className="h-9 px-2 text-xs"
                      onClick={() => setDefaultDuration(mins)}
                    >
                      {mins === 60 ? '1h' : `${mins}m`}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  disabled={defaultDuration === dbSettings?.default_appointment_duration || !defaultDuration}
                  onClick={() => updateSettings({ default_appointment_duration: defaultDuration })}
                >
                  Save
                </Button>
                {defaultDuration !== dbSettings?.default_appointment_duration && (
                  <span className="text-xs text-amber-600">Unsaved changes</span>
                )}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                This will be the default duration when creating new appointments.
              </p>
            </div>
          </div>

          {/* Personal Online Booking */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={20} />
              Online Booking (Personal Practice)
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.5rem' }}>
              Configure your personal availability for online booking. Patients can use the link below to book appointments directly.
            </p>

            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-lg">
              <Label className="text-emerald-800 font-semibold mb-2 block">Your Personal Booking Link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/book/doctor/${doctorId}`}
                  className="bg-white border-emerald-200 text-slate-700"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="bg-white text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/book/doctor/${doctorId}`);
                    toast.success("Link copied!");
                  }}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-emerald-600 mt-2">Add this link to your website or share it with patients.</p>
            </div>

            <form onSubmit={updateProfile}>
              <div className="mb-6">
                <Label htmlFor="personal_slot_duration_minutes" className="mb-2 block">Slot Duration (Minutes)</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="personal_slot_duration_minutes"
                    type="number"
                    step="1"
                    min="1"
                    max="120"
                    placeholder="30"
                    value={profile.personal_slot_duration_minutes || 30}
                    onChange={(e) => setProfile(prev => ({ ...prev, personal_slot_duration_minutes: parseInt(e.target.value) || 30 }))}
                    className="w-24 border-slate-300"
                  />
                  <div className="flex gap-1">
                    {[15, 30, 45, 60].map((mins) => (
                      <Button
                        key={mins}
                        type="button"
                        variant={profile.personal_slot_duration_minutes === mins ? "default" : "outline"}
                        size="sm"
                        className="h-9 px-2 text-xs border-slate-300"
                        onClick={() => setProfile(prev => ({ ...prev, personal_slot_duration_minutes: mins }))}
                      >
                        {mins === 60 ? '1h' : `${mins}m`}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="default_consultation_fee" className="mb-2 block">Default Consultation Fee (₹)</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="default_consultation_fee"
                    type="number"
                    step="1"
                    min="0"
                    placeholder="e.g. 500"
                    value={defaultFee}
                    onChange={(e) => setDefaultFee(e.target.value)}
                    className="w-32 border-slate-300"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!defaultFee && defaultFee !== "0" && defaultFee !== 0}
                    onClick={() => updateSettings({ default_consultation_fee: toPaise(defaultFee) })}
                  >
                    Save Fee
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">This fee will be displayed when patients book with you directly.</p>
              </div>

              <div>
                <Label className="mb-4 block">Weekly Availability</Label>
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                  <AvailabilityConfig
                    value={profile.personal_availability || {}}
                    onChange={(val) => setProfile(prev => ({ ...prev, personal_availability: val }))}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="mt-6 bg-slate-900 hover:bg-slate-800 text-white"
              >
                Save Availability
              </Button>
            </form>
          </div>

          {/* Preferences Section Removed as per request, but keeping backend logic intact */}

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CreditCard size={20} />
              Subscription & Limits
            </h2>

            <SubscriptionDetails doctorId={doctorId} />
          </div>

          {/* Notification Settings */}
          <div style={{ marginTop: '1.5rem' }}>
            {/* Info tooltip for notification settings */}
            <div style={{
              padding: "0.75rem 1rem",
              background: "#f0f9ff",
              borderRadius: "8px",
              border: "1px solid #bae6fd",
              display: "flex",
              alignItems: "flex-start",
              gap: "0.5rem",
              marginBottom: "1rem"
            }}>
              <Info size={16} style={{ color: "#0284c7", marginTop: "2px", flexShrink: 0 }} />
              <p style={{ fontSize: "0.8rem", color: "#0369a1", margin: 0, lineHeight: 1.4 }}>
                These notification settings apply only to <strong>personal appointments</strong>.
                For clinic appointments, update via the respective clinic&apos;s settings page.
              </p>
            </div>
            <NotificationSettings token={token} type="user" />
          </div>

          <div className="card" style={{ marginTop: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FileText size={20} />
              Invoice Branding
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.5rem' }}>
              Upload your signature and stamp to appear on generated invoices.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <ImageUpload
                token={token}
                uploadEndpoint="/profile/upload-url"
                updateEndpoint="/profile/images"
                downloadEndpoint="/profile/download-url"
                imageType="signature"
                currentUrl={dbProfile?.signature_url}
                label="Signature"
                onUploadComplete={(url) => {
                  if (dbProfile) {
                    db.users.update(doctorId, { signature_url: url });
                  }
                }}
              />
              <ImageUpload
                token={token}
                uploadEndpoint="/profile/upload-url"
                updateEndpoint="/profile/images"
                downloadEndpoint="/profile/download-url"
                imageType="stamp"
                currentUrl={dbProfile?.stamp_url}
                label="Stamp/Seal"
                onUploadComplete={(url) => {
                  if (dbProfile) {
                    db.users.update(doctorId, { stamp_url: url });
                  }
                }}
              />
            </div>
          </div>

          {/* Media Storage Usage */}
          <StorageUsageCard token={token} />
        </div>
      </div>
    </div >
  );
}

Settings.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};

function SubscriptionDetails({ doctorId }) {
  const subscription = useLiveQuery(async () => {
    if (!doctorId) return null;
    return await db.user_subscriptions.where('user_id').equals(doctorId).filter(s => s.is_active).first();
  }, [doctorId]);

  const plan = useLiveQuery(async () => {
    if (!subscription) return null;
    return await db.plans.get(subscription.plan_id);
  }, [subscription]);

  const ownedClinicsCount = useLiveQuery(() => db.clinics.where('owner_doctor_id').equals(doctorId).count());

  const isOwner = ownedClinicsCount > 0;
  const usageLabel = isOwner ? "Total Usage (Personal + Clinics)" : "Personal Usage";

  if (!subscription || !plan) {
    return <div className="text-sm text-slate-500 italic">No active subscription found.</div>;
  }

  return (
    <div>
      <div className="bg-slate-50 p-4 rounded-lg mb-6 flex justify-between items-center border border-slate-100">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Current Plan</div>
          <div className="text-xl font-bold text-slate-900">{plan.name}</div>
        </div>
        {(subscription.end_date) && (
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Validity Period</div>
            <div className="font-medium text-slate-700">
              {subscription.start_date ? (
                <>
                  {new Date(subscription.start_date).toLocaleDateString()} - {new Date(subscription.end_date).toLocaleDateString()}
                </>
              ) : (
                <>
                  Valid until {new Date(subscription.end_date).toLocaleDateString()}
                </>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Renews on {new Date(subscription.end_date).toLocaleDateString()}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <QuotaBar
          resource="appointments"
          label="Monthly Appointments"
          subtext={`${usageLabel}. count resets monthly.`}
        />
        <QuotaBar
          resource="patients"
          label="Monthly Patients"
          subtext={usageLabel}
        />
        <QuotaBar
          resource="clinics"
          label="Clinics"
        />
        <QuotaBar
          resource="members"
          label="Staff Members"
          subtext="Total members across all your clinics"
        />
      </div>

      <div className="mt-6 pt-4 border-t border-slate-100 text-center">
        <p className="text-xs text-slate-400">
          Need more capacity? Contact support to upgrade your plan or purchase add-ons.
        </p>
      </div>
    </div >
  );
}

SubscriptionDetails.propTypes = {
  doctorId: PropTypes.string
};